import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import { ThemeProvider } from 'styled-components';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import i18n, { i18nInitPromise } from './i18n/config';
import I18nLanguageSync from './i18n/I18nLanguageSync';
import './app/index.css';
import App from './app/App';
import { unregister } from './app/registerServiceWorker';
import { store } from './app/store';
import theme from './common/styles/theme';
import { applyApiErrorGrouping } from './common/helpers/apiError';

unregister();

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    // Release is injected at deploy time into /config.js (window.__RUNTIME_CONFIG__).
    // Explicit null check — optional chaining is not supported by our react-scripts.
    release: (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.RELEASE) || 'unknown',
    dsn: process.env.REACT_APP_SENTRY_DSN,
    ignoreErrors: [
      'Non-Error promise rejection captured', 'ResizeObserver loop limit exceeded'
    ],
    integrations: [new BrowserTracing()],
    // Required for per-route API issues: every ApiError shares one stack
    // origin, so only this fingerprint tells them apart.
    beforeSend: applyApiErrorGrouping,
    environment: process.env.REACT_APP_ENV,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: parseFloat(process.env.REACT_APP_TRACE_SAMPLING_RATE),
  });
}

if (process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'testing') {
  window.store = store;
}

const renderApp = () => {
  ReactDOM.render(
    <Provider store={store}>
      <CookiesProvider>
        <ThemeProvider theme={theme}>
          <I18nextProvider i18n={i18n}>
            <I18nLanguageSync />
            <App />
          </I18nextProvider>
        </ThemeProvider>
      </CookiesProvider>
    </Provider>,
    document.getElementById('main-view')
  );
};

// Wait for translations to load before rendering so all t() calls resolve to
// translated text on first render (prevents raw keys in machine dashboard etc.)
i18nInitPromise.then(renderApp).catch(renderApp);
